import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {IMAGE_ASSET, TREE_LEADERBOARD_TAB} from 'consts'
import {useTranslation} from 'i18n'
import {TreeLeaderboardData, TreeLeaderboardView} from 'types'
import {getBorder, getBoxShadow, getFontFamily, useHistory} from 'utils'
import {Icon, Modal, Paragraph, TabView} from 'common/components'
import {useDispatch} from 'lib/redux'
import convertUnit from 'lib/unit'
import {FototreeTallestTreeProps} from './FototreeTallestTreeProps'
import FototreeTallestTreeList from './FototreeTallestTreeList'

interface StyledTabItemProps {
  isSelected: boolean
}

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  width: fill-available;
  background-color: ${({theme}) => theme.primary_7};
  margin-bottom: ${convertUnit(-2)};
  padding: ${convertUnit(12)} ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledModal = styled(Modal)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 75%;
  max-width: ${convertUnit(480)};
  max-height: ${convertUnit(677)};
  margin: 0 ${convertUnit(20)};
  box-sizing: border-box;
  overflow: hidden;
  background: no-repeat center/cover
    url(${IMAGE_ASSET('giftshop', 'bg-tree.png')});
  background-color: ${({theme}) => theme.primary_7};
`

const StyledTabContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  gap: ${convertUnit(20)};
  padding: ${convertUnit(8)} ${convertUnit(20)};
  background-color: ${({theme}) => theme.primary_7};
  box-shadow: ${({theme}) =>
    getBoxShadow(theme, {
      verticalOffset: 2,
      blurRadius: 4,
      opacity: 0.24,
    })};
`

const StyledTabItem = styled.div<StyledTabItemProps>`
  color: ${({theme, isSelected}) =>
    isSelected ? theme.primary_2 : theme.gray_2};
  width: calc(33% - (${convertUnit(40)}));
  justify-items: center;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`

const StyledTabItemIndicator = styled.div<StyledTabItemProps>`
  ${({theme, isSelected}) => ({
    color: isSelected ? theme.primary_2 : theme.gray_2,
    borderBottom: getBorder(
      3,
      'solid',
      isSelected ? theme.primary_2 : 'transparent',
    ),
  })}
  position: absolute;
  bottom: 0;
  width: inherit;
`

const StyledTabItemLabel = styled(Paragraph)<StyledTabItemProps>`
  ${({isSelected}) => ({
    fontFamily: getFontFamily(isSelected ? 'quicksandBold' : 'quicksandMedium'),
  })}
  color: inherit;
  text-align: center;
`

export default function FototreeTallestTree({
  stateVisible,
}: FototreeTallestTreeProps) {
  const history = useHistory()
  const {update} = useDispatch()
  const {translate} = useTranslation()
  const [visible, setVisible] = stateVisible

  const stateType = useState<TreeLeaderboardView | undefined>('ALL')
  const [tabView, setTabView] = stateType

  const handleNavigateFotoTreeDetail = useCallback(
    (item: TreeLeaderboardData) => {
      update('lastTreeState', {videoIntro: true})
      history.push(
        'tree_fototree_detail',
        {
          treeId: item.type === 'ALL' ? item.id : item.tree_id,
          treeAlias: item.type === 'ALL' ? item.alias : item.tree_alias,
        },
        item.type === 'ALL' ? item.alias : item.tree_alias,
      )
    },
    [history, update],
  )

  const handleSetTabView = useCallback(
    (newTab: TreeLeaderboardView) => {
      if (tabView !== newTab) {
        setTabView(newTab)
      }
    },
    [setTabView, tabView],
  )

  const handleRenderTabTitle = useMemo(
    () => (
      <StyledTabContainer>
        {TREE_LEADERBOARD_TAB.map((tab) => (
          <StyledTabItem
            key={tab}
            isSelected={tabView === tab}
            onClick={() => handleSetTabView(tab)}>
            <StyledTabItemIndicator isSelected={tabView === tab} />
            <StyledTabItemLabel isSelected={tabView === tab}>
              {tab === 'ALL'
                ? translate('global:allTime')
                : translate('global:lastCertainDays', {
                    days: tab === 'LAST_30_DAYS' ? 30 : 7,
                  })}
            </StyledTabItemLabel>
          </StyledTabItem>
        ))}
      </StyledTabContainer>
    ),
    [handleSetTabView, tabView, translate],
  )

  const handleRenderHeader = useMemo(
    () => (
      <StyledHeader>
        <Icon
          type="close"
          color="white_1"
          style={{cursor: 'pointer'}}
          onClick={() => setVisible((prev) => !prev)}
        />
        <Paragraph fontSize="l" fontWeight="medium" color="white_1">
          {translate('tree:tallestTree')}
        </Paragraph>
      </StyledHeader>
    ),
    [setVisible, translate],
  )

  const handleRenderTabItem = useCallback(
    (key: TreeLeaderboardView) => (
      <FototreeTallestTreeList
        type={key}
        onClickItem={handleNavigateFotoTreeDetail}
      />
    ),
    [handleNavigateFotoTreeDetail],
  )

  const handleRenderTabView = useMemo(
    () => (
      <TabView
        showTab={false}
        persistContent
        routes={TREE_LEADERBOARD_TAB}
        stateSelect={stateType}
        renderItem={handleRenderTabItem}
        onClickItem={setTabView}
        containerStyle={{
          height: '100%',
          backgroundColor: 'transparent',
        }}
      />
    ),
    [stateType, handleRenderTabItem, setTabView],
  )

  return (
    <StyledModal
      visible={visible}
      onBackdropPress={() => setVisible((prev) => !prev)}>
      {handleRenderHeader}
      {handleRenderTabTitle}
      {handleRenderTabView}
    </StyledModal>
  )
}
